<script lang="ts">
	import TypicalFeatureStructure from '$lib/components/ui/feature/TypicalFeatureStructure.svelte';
	import QuickStat from '$lib/components/ui/QuickStat.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import type { Attraction } from '$lib/features/attraction/attraction.type';
	import { CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import { FULL_SITE_URL } from '$lib/statics';
	import { buildMetaDescriptionForStandalonePage, buildMetaTitleForStandalonePage } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Output } from './+page.server';

	const featureType = CityFeatureType.ATTRACTION;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const item = $derived<Attraction>(output.item);
	const location = $derived<PlaceLocation>(output.location);
	const meta = $derived.by(() => {
		const title = buildMetaTitleForStandalonePage({
			itemEnglishName: item.feature.englishName,
			cityName: location.city.label,
			isUnique: item.isUniqueName
		});
		const description = buildMetaDescriptionForStandalonePage({
			itemEnglishName: item.feature.englishName,
			cityName: location.city.label,
			isUnique: item.isUniqueName,
			place: item.feature.place,
			factsLength: item.facts.length,
			faqsLength: item.faqs.length,
			videosLength: item.feature.videos.length,
			durationHours: item.durationHours
		});
		return {
			title,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeature({ location, type: featureType, featureSlug: item.feature.slug }).href}`,
			imageUrl: UrlUtil.getFullImageUrl(item.feature.images[0])
		};
	});
	const combinedJsonLd = $derived([
		data.output.jsonLdBreadcrumbs,
		data.output.jsonLdTouristAttraction,
		data.output.jsonLdFAQPage
	]);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{#if item.feature.videos.length > 0}
		<link rel="preconnect" href="https://i.ytimg.com" crossorigin="anonymous" />
	{/if}

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<TypicalFeatureStructure
	{location}
	feature={item.feature}
	otherSameTypeItems={output.otherSameTypeItems}
	children={output.children}
	nearbyFeatures={output.nearbyFeatures}
	featureCollections={output.featureCollections}
	toursAndItinerariesContainingFeature={output.toursAndItinerariesContainingFeature}
	typeMapPoints={output.typeMapPoints}
	keepExploring={output.keepExploring}
	facts={item.facts}
	story={item.story}
	events={item.events}
	faqs={item.faqs}
	language={output.language}
	breadcrumbs={data.output.breadcrumbs}
>
	{#snippet ExtraQuickStatsSnippet()}
		{@render VisitDuration()}

		{#snippet VisitDuration()}
			<QuickStat label="EXPLORATION" value={`${item.durationHours}h`} title="Recommended visit duration" />
		{/snippet}
	{/snippet}
</TypicalFeatureStructure>
